import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTariff, setLoadingTariff] = useState(null);
  const [error, setError] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetch(`${API_BASE}/api/payments/tariffs`)
      .then(r => r.json())
      .then(data => setPlans(data))
      .catch(err => {
        console.error("Ошибка загрузки тарифов:", err);
        setError("Не удалось загрузить тарифы");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user?.plan) setCurrentPlan(user.plan);
  }, [user]);

  const buyTariff = async (tariff) => {
    if (tariff === 'free') return;
    setError(null);
    setLoadingTariff(tariff);

    try {
      const res = await fetch(`${API_BASE}/api/payments/create-payment`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tariff }),
      });

      if (res.status === 401) { navigate("/login"); return; }

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        setError(`Ошибка сервера: ${res.status}${text ? ". " + text : ""}`);
        return;
      }

      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setError("Сервер не вернул ссылку на оплату");
    } catch (err) {
      setError("Ошибка сети: " + err.message);
    } finally {
      setLoadingTariff(null);
    }
  };

  if (loading) {
    return (
        <div className="p-3 md:p-6">
        <div className="inline-block w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
      <div className="p-4 md:p-6">
      <h1 className="text-2xl md:text-4xl font-bold mb-2 md:mb-3">
        Тарифные планы
      </h1>
      <p className="text-gray-500 mb-6 md:mb-10 text-sm">
        Подберите подходящий план для ваших целей
      </p>

      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded-xl mb-4 text-sm">
          {error}
        </div>
      )}

      {plans.length === 0 && !loading && (
        <div className="text-center text-gray-500 p-10 text-sm">
          Тарифы не найдены
        </div>
      )}

      {/* Grid: 1 колонка на мобильном, auto-fit на планшете/десктопе */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {plans.map((plan) => {
          const isCurrent = currentPlan === plan.tariff;
          return (
            <div
              key={plan.tariff}
              className={`relative flex flex-col rounded-2xl p-5 md:p-6 bg-white ${
                plan.featured
                  ? 'border-2 border-blue-600'
                  : 'border border-gray-200'
              }`}
            >
              {plan.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                  Популярный
                </span>
              )}

              {isCurrent && (
                <span className="absolute -top-3 right-3 bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                  Текущий
                </span>
              )}

              <h2 className="text-lg md:text-xl font-bold">{plan.name}</h2>

              <div className="text-2xl md:text-3xl font-bold text-blue-600 mt-2">
                {plan.price_display}
              </div>
              <div className="text-xs md:text-sm text-gray-500 mb-4">
                {plan.period}
              </div>

              <ul className="list-none p-0 mb-5 flex-1 space-y-2">
                {plan.features.map((f, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="text-green-600 flex-shrink-0">✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {plan.tariff === 'free' ? (
                <button
                  disabled
                  className={`w-full py-2.5 rounded-xl font-bold text-sm ${
                    isCurrent
                      ? 'bg-green-100 text-green-800 cursor-default'
                      : 'bg-gray-100 text-gray-500 cursor-default'
                  }`}
                >
                  {isCurrent ? 'Активен' : 'Бесплатно'}
                </button>
              ) : (
                <button
                  onClick={() => buyTariff(plan.tariff)}
                  disabled={loadingTariff !== null || isCurrent}
                  className={`w-full py-2.5 rounded-xl font-bold text-sm transition ${
                    isCurrent
                      ? 'bg-green-100 text-green-800 cursor-default'
                      : loadingTariff === plan.tariff
                        ? 'bg-blue-300 cursor-wait text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {isCurrent
                    ? 'Активен'
                    : loadingTariff === plan.tariff
                      ? "Перенаправление…"
                      : "Оплатить"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}